export type PaintPoint = {
  x: number;
  y: number;
};

export function rasterizeLine(start: PaintPoint, end: PaintPoint): PaintPoint[] {
  const points: PaintPoint[] = [];
  let x0 = start.x;
  let y0 = start.y;
  const x1 = end.x;
  const y1 = end.y;
  const dx = Math.abs(x1 - x0);
  const sx = x0 < x1 ? 1 : -1;
  const dy = -Math.abs(y1 - y0);
  const sy = y0 < y1 ? 1 : -1;
  let error = dx + dy;

  while (true) {
    points.push({ x: x0, y: y0 });
    if (x0 === x1 && y0 === y1) {
      return points;
    }
    const doubleError = error * 2;
    if (doubleError >= dy) {
      error += dy;
      x0 += sx;
    }
    if (doubleError <= dx) {
      error += dx;
      y0 += sy;
    }
  }
}

export function rasterizeRectangleOutline(start: PaintPoint, end: PaintPoint): PaintPoint[] {
  const left = Math.min(start.x, end.x);
  const right = Math.max(start.x, end.x);
  const top = Math.min(start.y, end.y);
  const bottom = Math.max(start.y, end.y);
  const seen = new Set<string>();
  const points: PaintPoint[] = [];

  const addPoint = (x: number, y: number): void => {
    const key = `${x}:${y}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    points.push({ x, y });
  };

  for (let x = left; x <= right; x += 1) {
    addPoint(x, top);
    addPoint(x, bottom);
  }
  for (let y = top; y <= bottom; y += 1) {
    addPoint(left, y);
    addPoint(right, y);
  }

  return points;
}

export function rasterizeFloodFill(
  start: PaintPoint,
  width: number,
  height: number,
  readCode: (x: number, y: number) => number,
  replacementCode: number
): PaintPoint[] {
  if (start.x < 0 || start.x >= width || start.y < 0 || start.y >= height) {
    return [];
  }
  const targetCode = readCode(start.x, start.y);
  if (targetCode === replacementCode) {
    return [];
  }

  const points: PaintPoint[] = [];
  const queue: PaintPoint[] = [start];
  const visited = new Uint8Array(width * height);

  while (queue.length > 0) {
    const point = queue.pop()!;
    if (point.x < 0 || point.x >= width || point.y < 0 || point.y >= height) {
      continue;
    }
    const index = point.y * width + point.x;
    if (visited[index] !== 0) {
      continue;
    }
    visited[index] = 1;
    if (readCode(point.x, point.y) !== targetCode) {
      continue;
    }
    points.push(point);
    queue.push(
      { x: point.x + 1, y: point.y },
      { x: point.x - 1, y: point.y },
      { x: point.x, y: point.y + 1 },
      { x: point.x, y: point.y - 1 }
    );
  }

  return points;
}
